import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalActionType, ModalVariant } from '@/components/Modal';
import { RadioGroup, RadioGroupItem } from '@/components/ui/RadioGroup';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { cn } from '@/lib/utils';

type DeleteFolderChoice = 'folderOnly' | 'folderAndSources';

interface DeleteFolderConfirmationModalProps {
    isOpen: boolean;
    deleteInProgress?: boolean;
    sourceCount: number;
    onConfirm: (deleteSourcesToo: boolean) => void;
    onCancel: () => void;
}

export function DeleteFolderConfirmationModal({
    isOpen,
    deleteInProgress,
    sourceCount,
    onConfirm,
    onCancel,
}: DeleteFolderConfirmationModalProps) {
    const { t } = useTranslation(I18nNamespace.SOURCES);
    const [selectedOption, setSelectedOption] =
        useState<DeleteFolderChoice>('folderOnly');

    useEffect(() => {
        if (isOpen) {
            setSelectedOption('folderOnly');
        }
    }, [isOpen]);

    const handleSubmit = () => {
        onConfirm(selectedOption === 'folderAndSources');
    };

    return (
        <Modal
            isOpen={isOpen}
            title={t('folders.deleteFolderTitle')}
            onCancel={onCancel}
            onSubmit={handleSubmit}
            submitInProgress={deleteInProgress}
            submitText={
                selectedOption === 'folderOnly'
                    ? t('folders.deleteFolder')
                    : t('folders.deleteFolderConfirmSubmit')
            }
            size="medium"
            variant={ModalVariant.PLAIN}
            actionType={
                selectedOption === 'folderAndSources'
                    ? ModalActionType.DANGER
                    : ModalActionType.DEFAULT
            }
        >
            <div className="flex flex-col gap-[17px] px-6 pt-0 pb-6">
                <div className="flex flex-col gap-2">
                    <p className="text-sm font-normal leading-5 text-grey-300">
                        {t('folders.deleteFolderSourcesSubtitle')}
                    </p>
                </div>
                <RadioGroup
                    value={selectedOption}
                    onValueChange={(value) =>
                        setSelectedOption(value as DeleteFolderChoice)
                    }
                    className="flex flex-col rounded-lg"
                >
                    <label
                        className={cn(
                            'flex flex-row items-start gap-3 py-3 cursor-pointer',
                            'focus-within:outline-none',
                        )}
                    >
                        <RadioGroupItem
                            value="folderOnly"
                            id="delete-folder-only"
                        />
                        <span className="flex flex-1 flex-col gap-1">
                            <span className="text-sm font-medium leading-5 text-white-100">
                                {t('folders.deleteFolderOnly')}
                            </span>
                            <span className="text-xs font-medium leading-[18px] text-grey-300">
                                {t('folders.deleteFolderOnlyDescription')}
                            </span>
                        </span>
                    </label>
                    <label
                        className={cn(
                            'flex flex-row items-start gap-3 py-3 cursor-pointer',
                            'focus-within:outline-none',
                            sourceCount === 0 &&
                                'opacity-60 cursor-not-allowed',
                        )}
                    >
                        <RadioGroupItem
                            value="folderAndSources"
                            id="delete-folder-and-sources"
                            disabled={sourceCount === 0}
                        />
                        <span className="flex flex-1 flex-col gap-1">
                            <span className="text-sm font-medium leading-5 text-white-100">
                                {t('folders.deleteFolderAndSources')}
                            </span>
                            <span className="text-xs font-medium leading-[18px] text-grey-300">
                                {t('folders.deleteFolderAndSourcesDescription')}
                            </span>
                        </span>
                    </label>
                </RadioGroup>
            </div>
        </Modal>
    );
}
